import { PedagogyActionOrigin, ReactIcons } from "@kalyzee/escaet-ui";
import { MeetingRoom, MeetingRoomStatus, MeetingRoomUpdateRequest, MeetingSessionUpdateAlertsRequest, MeetingUserDataCallStatus } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useMemo } from "react";
import { createPairings, openRoomWithUsers, updateRoom, updateSessionAdminStats, updateUserData } from "../../../helpers/meeting";
import { hasBadBehaviors, pedagogyExtraUserDataToString } from "../../../helpers/pedagogy";
import { ConfirmableEscaetPedagogyAction, EscaetPedagogyAction, MeetingPedagogyUserData } from "../../../interfaces/pedagogy";
import { PedagogyConstants } from "../../../constants/pedagogy";
import { MeetingSession, MeetingSessionAlertState } from "@kalyzee/kast-websocket-module/dist/esm/interfaces";
import { toastInfo } from "../../../helpers/toast";

const userIsLate = (user: MeetingPedagogyUserData) => {
  return user.lateSince !== undefined;
};

interface PedagogyActionData {
  actionType: EscaetPedagogyAction["type"],
  warn: boolean,
  late: boolean,
  notify?: boolean;
  status?: MeetingUserDataCallStatus,
  message?: string; 
  markAsTreated?: boolean,
  allowBadBehaviors?: "until_update" | "forever",
}

export const usePedagogyActions = (
  pedaMeetingId?: string,
  pedaRoom?: MeetingRoom,
  selfUserId?: string,
  onConfirmableAction?: (action: ConfirmableEscaetPedagogyAction) => void,
)  => {
  const processAction = useCallback((
    users: MeetingPedagogyUserData[], 
    data: PedagogyActionData,
    customData?: (user: MeetingPedagogyUserData, currData: PedagogyActionData) =>  Partial<Omit<PedagogyActionData, "actionType">>, 
    requiresConfirmation?: boolean,
  ) => {
    if (requiresConfirmation) {
      if (data.allowBadBehaviors) {
        const toConfirm: ConfirmableEscaetPedagogyAction<"check"> = {
          actionType: data.actionType,
          dataType: "check",
          customMessage: "Tolérer jusqu'à la fin du cours",
          users,
          finalize: (checked) => {
            processAction(users, {
              ...data, 
              allowBadBehaviors: checked ? "forever" : "until_update",
            }, customData)
            return true;
          },
        };
        onConfirmableAction?.(toConfirm);
        return;
      } else {
        const toConfirm: ConfirmableEscaetPedagogyAction<"message"> = {
          actionType: data.actionType,
          dataType: "message",
          users,
          finalize: (message) => {
            if (!message?.trim()?.length && data.actionType === 'notify') {
              toastInfo('Veuillez écrire un message.')
              return false;
            }
            processAction(users, {
              ...data, 
              message: message ?? '',
            }, customData);
            return true;
          },
        }
        onConfirmableAction?.(toConfirm);
        return;
      }
    }
    users.forEach(async (user) => {
      const { id, meetingId } = user;
      if (!meetingId) return;
      const userData = {...data, ...(customData?.(user, data) ?? {})};
      let { status, late, warn, markAsTreated, message, allowBadBehaviors, notify } = userData;
      console.log("yoyoyoy")
      if (allowBadBehaviors && user.sessions) {
        console.log("yeyeyeye")
        user.sessions.forEach((session: MeetingSession) => {
          const alerts = session.alerts?.entries;
          if (!alerts) return;
          const entries: MeetingSessionUpdateAlertsRequest["entries"] = {};
          Object.entries((alerts)).forEach(([key, entry]) => {
            if (!(entry as any)?.value) return;
            (entries as any)[key] = {
              state: allowBadBehaviors === "forever" ? MeetingSessionAlertState.InactiveForever : MeetingSessionAlertState.InactiveUntilUpdate,
            };
          });
          if (!Object.keys(entries).length) return;
          updateSessionAdminStats(meetingId, session.id, entries);
        });
        return;
      }
      updateUserData(
        meetingId,
        {
          mode: "by_user_id",
          id,
        },
        {
          call: {
            status,
            extraData: pedagogyExtraUserDataToString({
              late,
              report: warn,
              notify
            }),
            seenByUser: false,
            userMessage: markAsTreated ? "" : undefined, // resets the user motive, removes him from the peda list
            message: message,
          },
        },
      );
    });
    // TODO : backend should handle this in an atomic way
    if (pedaRoom && data.markAsTreated) {
      const update: MeetingRoomUpdateRequest = {
        roomId: pedaRoom.id,
      };
      const newIds: string[] = [];
      pedaRoom.enlistedUserIds?.forEach(userId => {
        if (!users.some(user => user.id === userId)) {
          newIds.push(userId);
        }
      });
      if (newIds.length === 1 && newIds[0] === selfUserId) {
        update.status = MeetingRoomStatus.Closed;
      }
      update.enlistedUserIds = newIds;
      if (update.status || newIds.length !== pedaRoom.enlistedUserIds?.length) {
        // update only if needed
        updateRoom(update);
      }
    }
  }, [pedaMeetingId, pedaRoom, selfUserId, onConfirmableAction]);

  const inviteUsers = useCallback(async (toInvite: MeetingPedagogyUserData[]) => {
    if (!pedaMeetingId || !selfUserId) return;

    const isLate = toInvite.some(u => userIsLate(u));

    const toInviteCleaned = toInvite
      .filter(({ meetingId }) => meetingId)
      .map(({ id, meetingId }) => ({ id, fromMeetingId: meetingId }));

    const room = await openRoomWithUsers(
      pedaMeetingId, 
      [...toInviteCleaned.map(({ id }) => id), selfUserId], 
      [selfUserId], // main user
      isLate ? PedagogyConstants.WaitingRoomExtraData : undefined,
    );
    if (!room) return;

    // TODO : Maybe users are already here (for example auto peda pairing when late)
    createPairings(toInviteCleaned, pedaMeetingId, room.id);
  }, [pedaMeetingId, selfUserId]);

  const actions = useMemo<EscaetPedagogyAction[]>(() => ([
    {
      type: "exclude",
      name: (users: MeetingPedagogyUserData[]) => {
        if (users.every((u: MeetingPedagogyUserData) => u.inWaitingRoom)) return "Refuser l'entrée en cours";
        return "Exclure";
      },
      onExecute: (
        users: MeetingPedagogyUserData[]
      ) => processAction(
        users, 
        {
          actionType: "exclude",
          status: MeetingUserDataCallStatus.KICKED,
          late: true,
          warn: true,
          markAsTreated: true,
        },
        (user) => ({ 
          status: userIsLate(user) ? MeetingUserDataCallStatus.MISSING : MeetingUserDataCallStatus.KICKED, 
          late: userIsLate(user),
        }), 
        true,
      ),
      Icon: <ReactIcons.Cancel style={{ color: "#fff", width: "100%", height: "100%" }} />,
      color: "#FF6161",
    },
    {
      type: "warn",
      name: (users: MeetingPedagogyUserData[]) => {
        if (users.every((u: MeetingPedagogyUserData) => u.inWaitingRoom)) return "Autoriser avec avertissement";
        return "Avertir";
      },
      onExecute: (
        users: MeetingPedagogyUserData[]
      ) => processAction(
        users, 
        {
          actionType: "warn",
          status: MeetingUserDataCallStatus.ONLINE,
          late: false,
          warn: true,
          markAsTreated: true,
        },
        (user) => ({ 
          late: userIsLate(user),
        }),  
        true,
      ),
      Icon: <ReactIcons.Warning style={{ color: "#fff", width: "100%", height: "100%" }} />,
      color: "#FFA850",
    },

    {
      type: "contact",
      name: "Contacter",
      onExecute: inviteUsers,
      Icon: <ReactIcons.HeadphonePresence style={{ color: "#fff", width: "100%", height: "100%" }} />,
      color: "#3BD2C9",
    },
    {
      type: "notify",
      name: "Notifier",
      onExecute: (
        users: MeetingPedagogyUserData[],
      ) => processAction(
        users, 
        { 
          actionType: "notify",
          warn: false, 
          late: false,
          notify: true,
          markAsTreated: false,
        }, 
        undefined,
        true,
      ),
      Icon: <ReactIcons.ApprenantChatIcon style={{ color: "#fff", width: "100%", height: "100%" }} />,
      color: "#0090FF",
    },
    {
      type: "tolerate",
      name: (users: MeetingPedagogyUserData[]) => {
        if (users.every((u: MeetingPedagogyUserData) => u.inWaitingRoom)) return "Autoriser l'entrée en cours";
        return "Tolérer";
      },
      onExecute: (
        users: MeetingPedagogyUserData[],
        origin: PedagogyActionOrigin,
      ) => processAction(
        users, 
        { 
          actionType: "tolerate",
          status: MeetingUserDataCallStatus.ONLINE,
          warn: false, 
          late: true,
          markAsTreated: true,
          allowBadBehaviors: origin === PedagogyActionOrigin.BehaviorRoom ? "until_update" : undefined,
        }, 
        (user, currData) => {
          let message = currData.message;
          if (!message && !userIsLate(user)) {
            // set default message if user isn't late. It's important for student side!
            message = "La pédagogie a accepté votre retour en classe";
          }
          return { 
            late: userIsLate(user), 
            message,
          }
        },  
        true,
      ),
      Icon: <ReactIcons.PedagoCheck style={{ color: "#fff", width: "100%", height: "100%" }} />,
      color: "#05C496",
    },
  ]), [processAction, inviteUsers]);

  return actions;
}